import { useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useQueryClient } from 'react-query';
import { ENTITY_SLUGS, ListingsStateProvider, useListingStore } from 'modules/Listings/store/StateContext';
import { modelsListType, workflowsListType } from 'modules/Listings/store/Store.types';
import { createModelsList } from 'modules/Models/listingStore/createModelsList';
import { createWorkflowsList } from 'modules/Workflows/listingStore/createWorkflowsList';
import { Col, Row } from 'components/Grid';
import dynamic from 'next/dynamic';
import { SupportedListingEntities } from 'modules/Listings/types';
import { ListView } from './components/ListView';
import { NavBar } from './components/NavBar';
import { viewAllModalContent } from './styles';
import { SidebarFilterProps } from './components/FilterArea';

// If userId & appId left undefined, will query from community
// If userId & appId both provided, will query from user filtered by app
interface EntitySourceParams {
  params: string;
  /**  if only userId provided, will query from user from all their apps */
  userId?: string;
  /**  if providing appId, must provide userId as well, will query from user by app */
  appId?: string;
}

interface Props {
  onSelect?(entity: SupportedListingEntities): void;
  segment?: 'models' | 'workflows';
  modelsConfig?: EntitySourceParams;
  onClose?: () => unknown;
  // todo- add workflowConfig when needed
}

const FilterAreaDynamic = dynamic<SidebarFilterProps>(() => import('modules/Listings/components/FilterArea').then((mod) => mod.FilterArea), {
  ssr: false,
});

export const CommunitySearchModal = observer(({ onSelect, segment, modelsConfig, onClose }: Props): JSX.Element => {
  const [activeTab, setActiveTab] = useState(ENTITY_SLUGS.models);
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);

  const queryClient = useQueryClient();

  const handleOptionSelected = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (segment === 'models') {
      setActiveTab(ENTITY_SLUGS.models);
    } else if (segment === 'workflows') {
      setActiveTab(ENTITY_SLUGS.workflows);
    }
  }, []);

  const modelsStore = useListingStore<modelsListType>(
    useCallback(
      () =>
        createModelsList({
          queryClient,
          isStarred: false,
          initialQuery: {},
          overrideUrlClick: onSelect,
          hasFilters: true,
          disableRouterUpdates: true,
          modelsConfig,
        }),
      [],
    ),
    [],
  );

  const workflowsStore = useListingStore<workflowsListType>(
    useCallback(
      () =>
        createWorkflowsList({
          queryClient,
          userId: undefined,
          initialList: [],
          isStarred: false,
          initialQuery: {},
          disableRouterUpdates: true,
          overrideUrlClick: onSelect,
        }),
      [],
    ),
    [],
  );

  useEffect(() => {
    if (activeTab !== 'workflows') {
      modelsStore.load();
    }
  }, [modelsStore, activeTab]);

  useEffect(() => {
    if (activeTab !== 'models') {
      workflowsStore.load();
    }
  }, [workflowsStore, activeTab]);

  useEffect(() => {
    modelsStore.setShowFilter(true);
  }, []);

  return (
    <ListingsStateProvider store={activeTab === ENTITY_SLUGS.models ? modelsStore : workflowsStore} entityType={activeTab}>
      <div className={viewAllModalContent}>
        <NavBar
          activeTab={activeTab}
          onOptionSelected={handleOptionSelected}
          onClose={onClose}
          isFiltersOpen={isFiltersOpen}
          toggleFilterState={setIsFiltersOpen}
        />
        <Row paddingX="2.5rem">
          {activeTab === ENTITY_SLUGS.models && isFiltersOpen && (
            <Col sm={3} paddingRight="1.5rem">
              <FilterAreaDynamic />
            </Col>
          )}
          <Col sm={activeTab === ENTITY_SLUGS.models && isFiltersOpen ? 9 : 12}>
            <ListView pageName="inputs" />
          </Col>
        </Row>
      </div>
    </ListingsStateProvider>
  );
});
